import type { jsPDF } from 'jspdf';
import { AI_DISCLAIMER_COPY } from '../models/descriptions/ai-disclaimer';
import { GraphTopic } from '../models/ai-model';

export interface NuclearTopicPdfRow {
  key: string;
  label: string;
  description: string;
  value: number;
}

const BRAND = { r: 58, g: 86, b: 158 };
const INK = { r: 42, g: 42, b: 42 };
const BODY = { r: 70, g: 74, b: 82 };
const MUTED = { r: 120, g: 126, b: 136 };
const GRID = { r: 214, g: 220, b: 230 };
const BAND = { r: 243, g: 246, b: 251 };
const RULE = { r: 226, g: 232, b: 240 };

const MARGIN = 0.58;

/** Baseline-to-baseline step. Kept above the font's own height so lines never collide. */
function baselineStep(fontSizePt: number): number {
  return (fontSizePt / 72) * 1.35;
}

function contentBottom(pdf: jsPDF): number {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const lines = pdf.splitTextToSize(
    AI_DISCLAIMER_COPY.pdfNotice,
    pageWidth - MARGIN * 2
  );
  const footerBaseline =
    pageHeight - 0.28 - Math.max(0, lines.length - 1) * 0.12;
  return footerBaseline - 0.32;
}

function asLines(value: string | string[]): string[] {
  return Array.isArray(value) ? value : [value];
}

function formatShare(value: number): string {
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 0.05) {
    return `${rounded}%`;
  }
  return `${value.toFixed(1).replace('.', ',')}%`;
}

const EXPLANATION_ID_ALIASES: Record<string, string> = {
  RCT: 'RT',
};

export function buildNuclearTopicPdfRows(
  distribution: Record<string, number>,
  labels: Record<string, string>,
  explanations: GraphTopic[]
): NuclearTopicPdfRow[] {
  const byId = new Map(explanations.map((topic) => [topic.id, topic]));
  const knownKeys = Object.keys(labels).filter((key) => key in distribution);
  const extraKeys = Object.keys(distribution).filter((key) => !(key in labels));
  const keys = [...knownKeys, ...extraKeys];

  return keys
    .map((key) => {
      const explanation =
        byId.get(key) ?? byId.get(EXPLANATION_ID_ALIASES[key] ?? '');
      const value = Number(distribution[key]);
      return {
        key,
        label: labels[key] ?? explanation?.topic ?? key,
        description: explanation?.description ?? '',
        value: Number.isFinite(value) ? Math.max(0, value) : 0,
      };
    })
    .sort((a, b) => b.value - a.value);
}

/**
 * Page 1 is only the bar chart. Descriptions start on page 2 so the plot
 * can use the full page and the glossary is not squeezed under the axis.
 */
export function renderNuclearTopicsPdf(
  pdf: jsPDF,
  options: { rows: NuclearTopicPdfRow[]; subtitle?: string }
): void {
  drawChartPage(pdf, options.rows, options.subtitle);
  drawDescriptionPages(pdf, options.rows);
}

function drawChartPage(
  pdf: jsPDF,
  rows: NuclearTopicPdfRow[],
  subtitle?: string
): void {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const bottom = contentBottom(pdf);
  let y = MARGIN;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(BRAND.r, BRAND.g, BRAND.b);
  pdf.text('Distribuzione dei topic nucleari', pageWidth / 2, y + 0.18, {
    align: 'center',
  });
  y += 0.44;

  if (subtitle?.trim()) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(INK.r, INK.g, INK.b);
    const subtitleLines = asLines(
      pdf.splitTextToSize(subtitle.trim(), pageWidth - MARGIN * 2)
    );
    pdf.text(subtitleLines, pageWidth / 2, y, { align: 'center' });
    y += subtitleLines.length * baselineStep(10) + 0.04;
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
  pdf.text('Quota del colloquio dedicata a ciascun topic', pageWidth / 2, y, {
    align: 'center',
  });
  y += 0.28;

  if (!rows.length) {
    return;
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  const tickLabelWidth = pdf.getTextWidth('100%');

  pdf.setFontSize(10);
  const labelColumn = Math.max(
    ...rows.map((row) => pdf.getTextWidth(row.label))
  );
  const labelGap = tickLabelWidth / 2 + 0.12;
  const plotLeft = MARGIN + labelColumn + labelGap;
  const plotRight = pageWidth - MARGIN - tickLabelWidth / 2;
  const plotWidth = Math.max(plotRight - plotLeft, 1);
  const plotTop = y;
  const plotBottom = bottom - 0.22;
  const rowHeight = (plotBottom - plotTop) / rows.length;
  const barHeight = Math.min(0.38, rowHeight * 0.58);

  rows.forEach((row, index) => {
    const rowTop = plotTop + index * rowHeight;
    const rowCenter = rowTop + rowHeight / 2;

    if (index % 2 === 0) {
      pdf.setFillColor(BAND.r, BAND.g, BAND.b);
      pdf.rect(MARGIN, rowTop, pageWidth - MARGIN * 2, rowHeight, 'F');
    }

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(
      row.value > 0 ? INK.r : MUTED.r,
      row.value > 0 ? INK.g : MUTED.g,
      row.value > 0 ? INK.b : MUTED.b
    );
    pdf.text(row.label, plotLeft - labelGap, rowCenter, {
      align: 'right',
      baseline: 'middle',
    });
  });

  for (const tick of [0, 25, 50, 75, 100]) {
    const x = plotLeft + (tick / 100) * plotWidth;
    pdf.setDrawColor(GRID.r, GRID.g, GRID.b);
    pdf.setLineWidth(0.006);
    pdf.line(x, plotTop, x, plotBottom);
  }

  rows.forEach((row, index) => {
    const rowCenter = plotTop + index * rowHeight + rowHeight / 2;
    const barWidth = (Math.min(row.value, 100) / 100) * plotWidth;
    const barY = rowCenter - barHeight / 2;
    const share = formatShare(row.value);

    if (barWidth > 0.015) {
      const radius = Math.min(0.06, barHeight / 2, barWidth / 2);
      pdf.setFillColor(BRAND.r, BRAND.g, BRAND.b);
      pdf.roundedRect(plotLeft, barY, barWidth, barHeight, radius, radius, 'F');
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    const shareWidth = pdf.getTextWidth(share);
    const fitsInside = barWidth >= shareWidth + 0.16;

    if (row.value <= 0) {
      pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
      pdf.text(share, plotLeft + 0.08, rowCenter, { baseline: 'middle' });
    } else if (fitsInside) {
      pdf.setTextColor(255, 255, 255);
      pdf.text(share, plotLeft + barWidth - 0.08, rowCenter, {
        align: 'right',
        baseline: 'middle',
      });
    } else {
      pdf.setTextColor(BRAND.r, BRAND.g, BRAND.b);
      pdf.text(share, plotLeft + barWidth + 0.08, rowCenter, {
        baseline: 'middle',
      });
    }
  });

  pdf.setDrawColor(160, 170, 186);
  pdf.setLineWidth(0.012);
  pdf.line(plotLeft, plotBottom, plotRight, plotBottom);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
  for (const tick of [0, 25, 50, 75, 100]) {
    const x = plotLeft + (tick / 100) * plotWidth;
    pdf.text(`${tick}%`, x, plotBottom + 0.16, { align: 'center' });
  }

  pdf.setTextColor(0, 0, 0);
}

function drawDescriptionPages(pdf: jsPDF, rows: NuclearTopicPdfRow[]): void {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  const bottom = contentBottom(pdf);
  const percentColumn = 0.78;
  const textX = MARGIN + percentColumn;
  const textWidth = contentWidth - percentColumn;
  const titleSize = 12;
  const bodySize = 10;
  const titleToBody = 0.24;
  const bodyStep = baselineStep(bodySize);

  const openPage = (continued: boolean): number => {
    pdf.addPage();
    const titleY = MARGIN + 0.16;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(continued ? 13 : 16);
    pdf.setTextColor(BRAND.r, BRAND.g, BRAND.b);
    pdf.text('Descrizione Topic Nucleari', pageWidth / 2, titleY, {
      align: 'center',
    });

    let nextY = titleY + 0.28;
    if (!continued) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(MUTED.r, MUTED.g, MUTED.b);
      pdf.text(
        'Stesso ordine del grafico, dal topic più presente a quello meno presente.',
        pageWidth / 2,
        nextY,
        { align: 'center' }
      );
      nextY += 0.32;
    } else {
      nextY += 0.08;
    }
    return nextY;
  };

  let y = openPage(false);

  rows.forEach((row, index) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(bodySize);
    const descriptionLines = row.description
      ? asLines(pdf.splitTextToSize(row.description, textWidth))
      : [];
    const blockBottom =
      y +
      titleToBody +
      descriptionLines.length * bodyStep +
      (index < rows.length - 1 ? 0.23 : 0.04);

    if (blockBottom > bottom) {
      y = openPage(true);
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(titleSize);
    pdf.setTextColor(BRAND.r, BRAND.g, BRAND.b);
    pdf.text(formatShare(row.value), MARGIN + percentColumn - 0.12, y, {
      align: 'right',
    });

    pdf.setTextColor(INK.r, INK.g, INK.b);
    pdf.text(row.label, textX, y);

    if (descriptionLines.length) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(bodySize);
      pdf.setTextColor(BODY.r, BODY.g, BODY.b);
      let lineY = y + titleToBody;
      for (const line of descriptionLines) {
        pdf.text(line, textX, lineY);
        lineY += bodyStep;
      }
      y = lineY;
    } else {
      y += titleToBody;
    }

    if (index < rows.length - 1) {
      y += 0.05;
      pdf.setDrawColor(RULE.r, RULE.g, RULE.b);
      pdf.setLineWidth(0.008);
      pdf.line(MARGIN, y, MARGIN + contentWidth, y);
      y += 0.18;
    }
  });

  pdf.setTextColor(0, 0, 0);
}
